"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.syncWithCis = syncWithCis;
var _merge = _interopRequireDefault(require("lodash/merge"));
var _pulseCookie = require("./pulse-cookie");
var _crossReference = _interopRequireDefault(require("./crossReference"));
var _cisParallelRequest = _interopRequireDefault(require("./cisParallelRequest"));
var _config = _interopRequireDefault(require("./config"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
/* eslint-env browser */
/**
 * Call CIS with the current user information.
 *
 * @private
 */
function identify() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    resolvedJwe = _ref.resolvedJwe,
    userId = _ref.userId,
    anId = _ref.anId,
    environmentId = _ref.environmentId,
    cisLocation = _ref.cisLocation;
  var jwe = resolvedJwe;
  return (0, _cisParallelRequest["default"])(cisLocation + _config["default"].cisBasePath, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json'
    }),
    body: JSON.stringify({
      jwe: jwe,
      userId: userId,
      anId: anId,
      environmentId: jwe ? undefined : environmentId
    }),
    credentials: 'include'
  });
}
var currentCisRequest = Promise.resolve();
var nativeJweSyncState = {
  syncWithAppNexus: false,
  syncWithCis: true,
  waitForCis: true
};

/**
 * Call CIS serially if needed, 'needed' being evaluated when the call reaches the top of the queue.
 *
 * @private
 */
function serialSyncWithCis(userId) {
  var forceSync = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var cisLocation = arguments.length > 2 ? arguments[2] : undefined;
  var nativeJwe = arguments.length > 3 ? arguments[3] : undefined;
  currentCisRequest = currentCisRequest.then(function () {
    var _parsePulseCookies = (0, _pulseCookie.parsePulseCookies)(),
      jwe = _parsePulseCookies.jwe,
      anId = _parsePulseCookies.anId,
      environmentId = _parsePulseCookies.environmentId;
    var state = nativeJwe ? nativeJweSyncState : (0, _pulseCookie.getSyncState)(userId);
    var resolvedJwe = nativeJwe || jwe;
    if (state.syncWithCis || forceSync) {
      return identify({
        resolvedJwe: resolvedJwe,
        userId: userId,
        anId: anId,
        environmentId: environmentId,
        cisLocation: cisLocation
      }).then(function (response) {
        return response.json();
      }).then(function (_ref2) {
        var data = _ref2.data;
        (0, _pulseCookie.serializePulseCookie)((0, _merge["default"])({}, data, {
          prevUserId: userId
        }));
        (0, _crossReference["default"])(data.crossReference);
        return Promise.resolve();
      });
    }
    return Promise.resolve();
  });
  return currentCisRequest;
}

/**
 * Sync with CIS
 *
 * An optional user id should be provided if a tracker have received a logged in state. Providing a new user id (different than a previous user id, serialized to the pulse cookie) will cause a "blocking call" to CIS (the returned promise will not resolve before the communication is done). This is done because CIS could provide new data regarding opt out.
 *
 * The returned promise will also be blocking if no pulse cookie is found. Pulse requires environmentIds generated server side so one have to wait to be allocated to one.
 *
 * In other context CIS could or could not be called but the returned promise will resolve immediately. If CIS have been called recently there is not point to call it again. In a different case, if a refresh call to CIS is required it doesn't mean that other systems have to wait of it. Both cases will return a promise that resolves more or less immediately.
 *
 * @returns {Promise} Events should be blocked until the returned promise
 * resolves.
*
 */
function syncWithCis(userId, nativeJwe, userServiceLocation) {
  var _ref3 = nativeJwe !== undefined ? nativeJweSyncState : (0, _pulseCookie.getSyncState)(userId),
    _syncWithCis = _ref3.syncWithCis,
    waitForCis = _ref3.waitForCis;
  var cisLocation = userServiceLocation || _config["default"].cisBaseUrl;
  var cisRequest = _syncWithCis ? serialSyncWithCis(userId, false, cisLocation, nativeJwe)["catch"](function () {
    return Promise.resolve();
  }) : Promise.resolve();

  // If `waitForCis` is false the update with CIS is only a "soft sync" and
  // shouldn't block other operations. This is the case when the browser has a
  // pulse2data cookie and the user haven't logged in or out. When the user do
  // log in we have to wait to avoid sending events with user ids after logging
  // out or without ids after logging in.
  var syncProgress = waitForCis ? cisRequest.then(function () {
    return (0, _pulseCookie.parsePulseCookies)();
  }) : Promise.resolve((0, _pulseCookie.parsePulseCookies)());
  return syncProgress;
}

// LocalWords: CIS